// Web Source Uploader - Modular component for scraping websites
import { useState } from 'react'
import { Globe, Loader2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

export interface CrawlConfig {
  maxDepth: number
  maxPages: number
  includePaths?: string[]
  excludePaths?: string[]
}

interface WebSourceUploaderProps {
  onSubmit: (url: string, crawlConfig?: CrawlConfig) => Promise<void>
  isLoading?: boolean
  error?: string | null
  disabled?: boolean
}

export function WebSourceUploader({
  onSubmit,
  isLoading = false,
  error = null,
  disabled = false,
}: WebSourceUploaderProps) {
  const [url, setUrl] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)
  const [crawlEnabled, setCrawlEnabled] = useState(true)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [crawlConfig, setCrawlConfig] = useState<CrawlConfig>({
    maxDepth: 2,
    maxPages: 20,
  })
  const [includePathsInput, setIncludePathsInput] = useState('')
  const [excludePathsInput, setExcludePathsInput] = useState('')

  const validateUrl = (value: string): boolean => {
    if (!value.trim()) {
      setUrlError('URL is required')
      return false
    }
    try {
      const parsed = new URL(value)
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        setUrlError('URL must start with http:// or https://')
        return false
      }
      setUrlError(null)
      return true
    } catch {
      setUrlError('Please enter a valid URL')
      return false
    }
  }

  const handleSubmit = async () => {
    if (!validateUrl(url)) return

    // Build crawl config with paths
    const config: CrawlConfig = crawlEnabled
      ? {
          ...crawlConfig,
          includePaths: includePathsInput
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          excludePaths: excludePathsInput
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        }
      : { maxDepth: 1, maxPages: 1 }

    await onSubmit(url, config)
  }

  return (
    <div className="space-y-4">
      {/* URL Input */}
      <div className="space-y-2">
        <Label htmlFor="url">Website URL</Label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
          <Input
            id="url"
            placeholder="https://company.com"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value)
              if (urlError) validateUrl(e.target.value)
            }}
            onBlur={() => url && validateUrl(url)}
            className={cn('pl-10', urlError && 'border-red-500')}
            disabled={isLoading || disabled}
          />
        </div>
        {urlError && <p className="text-xs text-red-500">{urlError}</p>}
        <p className="text-xs text-text-tertiary">Enter a URL to scrape and extract business intelligence</p>
      </div>

      {/* Crawl Options */}
      <div className="space-y-3 p-3 bg-warm-50 rounded-lg border border-border-subtle">
        <div className="flex items-center gap-2">
          <Checkbox
            id="crawl-enabled"
            checked={crawlEnabled}
            onCheckedChange={(checked) => setCrawlEnabled(checked as boolean)}
            disabled={isLoading || disabled}
          />
          <Label htmlFor="crawl-enabled" className="text-sm font-medium cursor-pointer">
            Crawl linked pages
          </Label>
        </div>

        {crawlEnabled && (
          <div className="space-y-3 pl-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-text-secondary">Max Depth</Label>
                <Select
                  value={String(crawlConfig.maxDepth)}
                  onValueChange={(v) => setCrawlConfig((c) => ({ ...c, maxDepth: parseInt(v) }))}
                  disabled={isLoading || disabled}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 level</SelectItem>
                    <SelectItem value="2">2 levels</SelectItem>
                    <SelectItem value="3">3 levels</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-text-secondary">Max Pages</Label>
                <Select
                  value={String(crawlConfig.maxPages)}
                  onValueChange={(v) => setCrawlConfig((c) => ({ ...c, maxPages: parseInt(v) }))}
                  disabled={isLoading || disabled}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 pages</SelectItem>
                    <SelectItem value="10">10 pages</SelectItem>
                    <SelectItem value="20">20 pages</SelectItem>
                    <SelectItem value="50">50 pages</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-text"
            >
              {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Advanced options
            </button>

            {showAdvanced && (
              <div className="space-y-2">
                <div className="space-y-1">
                  <Label className="text-xs text-text-secondary">Include paths (comma-separated)</Label>
                  <Input
                    placeholder="/products, /about, /pricing"
                    className="h-8 text-sm"
                    value={includePathsInput}
                    onChange={(e) => setIncludePathsInput(e.target.value)}
                    disabled={isLoading || disabled}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-text-secondary">Exclude paths (comma-separated)</Label>
                  <Input
                    placeholder="/blog, /careers, /news"
                    className="h-8 text-sm"
                    value={excludePathsInput}
                    onChange={(e) => setExcludePathsInput(e.target.value)}
                    disabled={isLoading || disabled}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <Button onClick={handleSubmit} disabled={isLoading || disabled || !url.trim()} className="w-full">
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {crawlEnabled ? 'Crawling...' : 'Scraping...'}
          </>
        ) : (
          <>
            <Globe className="mr-2 h-4 w-4" />
            {crawlEnabled ? 'Crawl & Analyze Website' : 'Scrape & Analyze Page'}
          </>
        )}
      </Button>
    </div>
  )
}
